import React, { useState, useEffect, useRef, useCallback } from "react";
import "./BasketballFlick.css";

const BasketballFlick = ({ onBack, isPaused }) => {
    const [score, setScore] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [feedback, setFeedback] = useState("");
    const [dimensions, setDimensions] = useState({ width: window.innerWidth, height: window.innerHeight });

    const CAN_W = 400;
    const CAN_H = 650;
    
    // Hoop logic
    const rimX = 200;
    const rimY = 200;
    const rimW = 80;

    const game = useRef({
        ball: { x: 200, y: 550, vx: 0, vy: 0, active: false, initialX: 200, initialY: 550, radius: 25, scored: false },
        lastFrame: 0,
        streak: 0
    });

    const touchRef = useRef({ startX: 0, startY: 0, currentX: 0, currentY: 0, time: 0, isTouching: false });
    const canvasRef = useRef(null);
    const audioCtxRef = useRef(null);

    // Warm up Audio Context to satisfy browser autoplay restrictions
    const warmUpAudio = () => {
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (!AudioContext) return;
            if (!audioCtxRef.current) {
                audioCtxRef.current = new AudioContext();
            }
            if (audioCtxRef.current.state === "suspended") {
                audioCtxRef.current.resume();
            }
        } catch (e) {
            console.warn("Audio Context warmup failed:", e);
        }
    };

    // Procedural sound synthesizer using Web Audio API
    const playBBSound = (type) => {
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (!AudioContext) return;
            let actx = audioCtxRef.current;
            if (!actx) {
                actx = new AudioContext();
                audioCtxRef.current = actx;
            }
            if (actx.state === "suspended") {
                actx.resume();
            }
            const now = actx.currentTime;

            if (type === "flick") {
                // Whippy whoosh
                const osc = actx.createOscillator();
                const gain = actx.createGain();
                osc.connect(gain);
                gain.connect(actx.destination);
                osc.type = "triangle";
                osc.frequency.setValueAtTime(80, now);
                osc.frequency.exponentialRampToValueAtTime(500, now + 0.12);
                gain.gain.setValueAtTime(0.15, now);
                gain.gain.exponentialRampToValueAtTime(0.01, now + 0.12);
                osc.start(now);
                osc.stop(now + 0.13);
            } else if (type === "bounce") {
                // Resonant metallic clack
                const osc = actx.createOscillator();
                const gain = actx.createGain();
                osc.connect(gain);
                gain.connect(actx.destination);
                osc.type = "sine";
                osc.frequency.setValueAtTime(320, now);
                osc.frequency.setValueAtTime(380, now + 0.01);
                gain.gain.setValueAtTime(0.3, now);
                gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
                osc.start(now);
                osc.stop(now + 0.26);
            } else if (type === "floor") {
                // Low rubber bounce thump
                const osc = actx.createOscillator();
                const gain = actx.createGain();
                osc.connect(gain);
                gain.connect(actx.destination);
                osc.type = "sine";
                osc.frequency.setValueAtTime(65, now);
                osc.frequency.exponentialRampToValueAtTime(30, now + 0.18);
                gain.gain.setValueAtTime(0.4, now);
                gain.gain.exponentialRampToValueAtTime(0.01, now + 0.18);
                osc.start(now);
                osc.stop(now + 0.19);
            } else if (type === "swish") {
                // High-fidelity bandpass-filtered noise friction net sound + bass hum
                const bufferSize = actx.sampleRate * 0.35;
                const buffer = actx.createBuffer(1, bufferSize, actx.sampleRate);
                const data = buffer.getChannelData(0);
                for (let i = 0; i < bufferSize; i++) {
                    data[i] = Math.random() * 2 - 1;
                }
                const noiseNode = actx.createBufferSource();
                noiseNode.buffer = buffer;

                const filter = actx.createBiquadFilter();
                filter.type = "bandpass";
                filter.Q.setValueAtTime(2.5, now);
                filter.frequency.setValueAtTime(1400, now);
                filter.frequency.exponentialRampToValueAtTime(800, now + 0.3);

                const gain = actx.createGain();
                gain.gain.setValueAtTime(0.28, now);
                gain.gain.exponentialRampToValueAtTime(0.01, now + 0.32);

                noiseNode.connect(filter);
                filter.connect(gain);
                gain.connect(actx.destination);

                noiseNode.start(now);
                noiseNode.stop(now + 0.35);

                const osc = actx.createOscillator();
                const oscGain = actx.createGain();
                osc.type = "sine";
                osc.frequency.setValueAtTime(220, now);
                osc.frequency.exponentialRampToValueAtTime(100, now + 0.25);
                oscGain.gain.setValueAtTime(0.12, now);
                oscGain.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
                osc.connect(oscGain);
                oscGain.connect(actx.destination);
                osc.start(now);
                osc.stop(now + 0.26);
            }
        } catch (e) {
            console.warn("Sound play failed:", e);
        }
    };

    // Track dynamic viewport dimensions
    useEffect(() => {
        const handleResize = () => {
            setDimensions({ width: window.innerWidth, height: window.innerHeight });
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const resetBall = () => {
        const ball = game.current.ball;
        ball.x = ball.initialX;
        ball.y = ball.initialY;
        ball.vx = 0; ball.vy = 0;
        ball.active = false;
        ball.scored = false;
        setTimeout(() => setFeedback(""), 1500);
    };

    const update = useCallback((time) => {
        if (!gameStarted || gameOver) return;
        if (isPaused || showInfo) {
            game.current.lastFrame = time;
            requestAnimationFrame(update);
            return;
        }
        if (game.current.lastFrame === 0) game.current.lastFrame = time;
        const dt = Math.min(2, (time - game.current.lastFrame) / 16);
        game.current.lastFrame = time;

        const ball = game.current.ball;
        if (ball.active) {
            ball.x += ball.vx * dt;
            ball.y += ball.vy * dt;
            ball.vy += 0.4 * dt; // Gravity

            // Virtual boundaries logic
            const scale = Math.max(0.5, 1 - (550 - ball.y) / 800);
            const currentRadius = ball.radius * scale;

            if (ball.x < currentRadius) {
                ball.x = currentRadius;
                ball.vx *= -0.8;
                playBBSound("bounce");
            } else if (ball.x > CAN_W - currentRadius) {
                ball.x = CAN_W - currentRadius;
                ball.vx *= -0.8;
                playBBSound("bounce");
            }

            // Backboard collision
            if (ball.vy < 0 && ball.y - currentRadius < 180 && ball.y > 80 && ball.x > 140 && ball.x < 260) {
                ball.vy *= -0.5;
                ball.vx *= 0.8;
                ball.y = 180 + currentRadius;
                playBBSound("bounce");
            }

            // Hoop collision - only when falling
            if (ball.vy > 0 && ball.y > rimY - 15 && ball.y < rimY + 15) {
                const distLeftRim = Math.hypot(ball.x - (rimX - rimW / 2), ball.y - rimY);
                const distRightRim = Math.hypot(ball.x - (rimX + rimW / 2), ball.y - rimY);
                
                if (distLeftRim < currentRadius + 5) {
                    ball.vx = Math.abs(ball.vx) * 0.5 + (ball.x < rimX - rimW / 2 ? -2 : 2);
                    ball.vy *= -0.6;
                    ball.y = rimY - currentRadius - 5;
                    playBBSound("bounce");
                } else if (distRightRim < currentRadius + 5) {
                    ball.vx = -Math.abs(ball.vx) * 0.5 + (ball.x > rimX + rimW / 2 ? 2 : -2);
                    ball.vy *= -0.6;
                    ball.y = rimY - currentRadius - 5;
                    playBBSound("bounce");
                } else if (!ball.scored && ball.x > rimX - rimW / 2 + 10 && ball.x < rimX + rimW / 2 - 10) {
                    setScore(s => s + 1);
                    setFeedback("SWISH! 🏀🔥");
                    ball.vx *= 0.3;
                    ball.scored = true;
                    playBBSound("swish");
                }
            }

            if (ball.y > CAN_H + 50) {
                if (!ball.scored) {
                    setFeedback("MISSED! ❌");
                }
                playBBSound("floor");
                resetBall();
            }
        }

        render();
        requestAnimationFrame(update);
    }, [gameStarted, gameOver, isPaused, dimensions]);

    // Translate dynamic canvas coordinates back to uniform logical scale (400 x 650)
    const getVirtualCoords = (clientX, clientY) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: clientX, y: clientY };
        const rect = canvas.getBoundingClientRect();
        
        const viewW = rect.width;
        const viewH = rect.height;
        
        const scale = Math.min(viewW / CAN_W, viewH / CAN_H);
        const offsetX = (viewW - CAN_W * scale) / 2;
        const offsetY = (viewH - CAN_H * scale) / 2;
        
        const canvasX = clientX - rect.left;
        const canvasY = clientY - rect.top;
        
        return {
            x: (canvasX - offsetX) / scale,
            y: (canvasY - offsetY) / scale
        };
    };

    const handleStart = (e) => {
        if (!gameStarted || game.current.ball.active) return;
        warmUpAudio();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const { x, y } = getVirtualCoords(clientX, clientY);
        
        touchRef.current = {
            startX: x,
            startY: y,
            currentX: x,
            currentY: y,
            time: performance.now(),
            isTouching: true
        };
    };

    const handleMove = (e) => {
        if (!touchRef.current.isTouching) return;
        if (e.cancelable) e.preventDefault();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const { x, y } = getVirtualCoords(clientX, clientY);
        touchRef.current.currentX = x;
        touchRef.current.currentY = y;
    };

    const handleEnd = (e) => {
        if (!gameStarted || game.current.ball.active || !touchRef.current.isTouching) return;
        touchRef.current.isTouching = false;
        
        const clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
        const clientY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
        const { x, y } = getVirtualCoords(clientX, clientY);
        
        const dx = x - touchRef.current.startX;
        const dy = y - touchRef.current.startY;
        const dt = Math.max(15, performance.now() - touchRef.current.time);

        if (dy > -10) return;

        let vx = (dx / dt) * 14;
        let vy = (dy / dt) * 14;

        vx = Math.max(-15, Math.min(15, vx));
        vy = Math.max(-28, Math.min(-14, vy));

        game.current.ball.vx = vx;
        game.current.ball.vy = vy;
        game.current.ball.active = true;
        game.current.ball.scored = false;
        setFeedback("");
        playBBSound("flick");
    };

    const render = () => {
        const ctx = canvasRef.current?.getContext("2d");
        if (!ctx) return;

        const scale = Math.min(dimensions.width / CAN_W, dimensions.height / CAN_H);
        const offsetX = (dimensions.width - CAN_W * scale) / 2;
        const offsetY = (dimensions.height - CAN_H * scale) / 2;

        ctx.clearRect(0, 0, dimensions.width, dimensions.height);

        // 1. Dynamic Fullscreen Stadium Crowd Background
        ctx.fillStyle = "#090a12";
        ctx.fillRect(0, 0, dimensions.width, dimensions.height);

        // Stadium ambient spotlight glow centered near backboard
        const spotGrad = ctx.createRadialGradient(
            dimensions.width / 2, 
            dimensions.height * 0.25, 
            20, 
            dimensions.width / 2, 
            dimensions.height * 0.25, 
            dimensions.width * 0.85
        );
        spotGrad.addColorStop(0, "#191a2e");
        spotGrad.addColorStop(0.5, "#0b0c15");
        spotGrad.addColorStop(1, "#030408");
        ctx.fillStyle = spotGrad;
        ctx.fillRect(0, 0, dimensions.width, dimensions.height);

        // Symmetrical vertical steel structural beams
        ctx.strokeStyle = "rgba(255, 255, 255, 0.02)";
        ctx.lineWidth = 3;
        const numBeams = 14;
        for (let i = 0; i <= numBeams; i++) {
            const x = (dimensions.width / numBeams) * i;
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(dimensions.width / 2 + (x - dimensions.width / 2) * 1.5, dimensions.height);
            ctx.stroke();
        }

        // 2. Translate coordinates to Virtual Space for Court Rendering
        ctx.save();
        ctx.translate(offsetX, offsetY);
        ctx.scale(scale, scale);

        // 3D Perspective maple wood court floor trapezoid
        ctx.fillStyle = "#d97706";
        ctx.beginPath();
        ctx.moveTo(80, 380);
        ctx.lineTo(320, 380);
        ctx.lineTo(400, 650);
        ctx.lineTo(0, 650);
        ctx.closePath();
        ctx.fill();

        // Floor joints & wood board stripes
        ctx.strokeStyle = "rgba(78, 30, 2, 0.22)";
        ctx.lineWidth = 1;
        for (let y = 380; y <= 650; y += 15) {
            ctx.beginPath();
            const leftX = 80 + (0 - 80) * ((y - 380) / (650 - 380));
            const rightX = 320 + (400 - 320) * ((y - 380) / (650 - 380));
            ctx.moveTo(leftX, y);
            ctx.lineTo(rightX, y);
            ctx.stroke();
        }
        for (let offset = -160; offset <= 160; offset += 40) {
            ctx.beginPath();
            ctx.moveTo(200 + offset * 0.6, 380);
            ctx.lineTo(200 + offset, 650);
            ctx.stroke();
        }

        // Low-opacity white perspective court lines
        ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
        ctx.lineWidth = 3;
        // Key paint boundary
        ctx.beginPath();
        ctx.moveTo(130, 380);
        ctx.lineTo(270, 380);
        ctx.lineTo(310, 650);
        ctx.lineTo(90, 650);
        ctx.closePath();
        ctx.stroke();

        // Free throw lane semi-circle
        ctx.beginPath();
        ctx.ellipse(200, 500, 60, 24, 0, 0, Math.PI * 2);
        ctx.stroke();

        // --- 3D Stadium Enclosure Safety Cage (Back, Left, Right Netting) ---
        const drawNetPanel = (x1, y1, x2, y2, h1, h2, highlightColor) => {
            ctx.save();
            // Define wall polygon
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.lineTo(x2, y2 - h2);
            ctx.lineTo(x1, y1 - h1);
            ctx.closePath();
            
            // Translucent glass fill with vertical gradient
            const wallGrad = ctx.createLinearGradient(0, Math.max(y1, y2), 0, Math.min(y1 - h1, y2 - h2));
            wallGrad.addColorStop(0, "rgba(249, 115, 22, 0.08)"); // Soft orange glow at base
            wallGrad.addColorStop(1, "rgba(59, 130, 246, 0.02)");  // Soft blue glow at top
            ctx.fillStyle = wallGrad;
            ctx.fill();
            
            // Clip mesh pattern inside the panel
            ctx.clip();
            
            // Criss-cross diagonal steel netting strands
            ctx.strokeStyle = "rgba(255, 255, 255, 0.12)";
            ctx.lineWidth = 1;
            const gridSpacing = 15;
            for (let offset = -600; offset <= 600; offset += gridSpacing) {
                // Down-Right lines
                ctx.beginPath();
                ctx.moveTo(-200, -200 + offset);
                ctx.lineTo(600, 600 + offset);
                ctx.stroke();
                
                // Down-Left lines
                ctx.beginPath();
                ctx.moveTo(600, -200 + offset);
                ctx.lineTo(-200, 600 + offset);
                ctx.stroke();
            }
            ctx.restore();
            
            // Draw support frame borders (Railing)
            ctx.strokeStyle = "#475569"; // Slate 600 steel base
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
            
            // Glowing neon top handrail
            ctx.strokeStyle = highlightColor;
            ctx.lineWidth = 2.5;
            ctx.shadowColor = highlightColor;
            ctx.shadowBlur = 5;
            ctx.beginPath();
            ctx.moveTo(x1, y1 - h1);
            ctx.lineTo(x2, y2 - h2);
            ctx.stroke();
            ctx.shadowBlur = 0;
            
            // Draw structural posts at start and end
            ctx.strokeStyle = "#64748b"; // Slate 500
            ctx.lineWidth = 2.5;
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x1, y1 - h1);
            ctx.moveTo(x2, y2);
            ctx.lineTo(x2, y2 - h2);
            ctx.stroke();
        };

        // Draw Back Net Panel
        drawNetPanel(80, 380, 320, 380, 140, 140, "#f97316");

        // Draw Left Net Panel (scales from 140px height at back to 240px at front)
        drawNetPanel(80, 380, 0, 650, 140, 240, "#f97316");

        // Draw Right Net Panel (scales from 140px height at back to 240px at front)
        drawNetPanel(320, 380, 400, 650, 140, 240, "#f97316");
        
        // Add middle support posts for extra 3D structure on the sides
        ctx.strokeStyle = "#64748b";
        ctx.lineWidth = 2.5;
        // Left middle post: base (40, 515), height 190
        ctx.beginPath();
        ctx.moveTo(40, 515);
        ctx.lineTo(40, 515 - 190);
        ctx.stroke();
        // Right middle post: base (360, 515), height 190
        ctx.beginPath();
        ctx.moveTo(360, 515);
        ctx.lineTo(360, 515 - 190);
        ctx.stroke();

        // 3D Glowing Glass Backboard
        ctx.save();
        ctx.fillStyle = "rgba(255, 255, 255, 0.08)";
        ctx.fillRect(120, 80, 160, 100);
        
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 4;
        ctx.shadowColor = "#ffffff";
        ctx.shadowBlur = 10;
        ctx.strokeRect(120, 80, 160, 100);
        ctx.shadowBlur = 0;

        ctx.strokeStyle = "#ff6b00";
        ctx.lineWidth = 3;
        ctx.shadowColor = "#ff6b00";
        ctx.shadowBlur = 8;
        ctx.strokeRect(165, 125, 70, 45);
        ctx.restore();

        // Steel Support brackets
        ctx.strokeStyle = "#94a3b8";
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.moveTo(200, 170);
        ctx.lineTo(200, 190);
        ctx.stroke();

        // Mesh Netting (White strand polygons)
        ctx.strokeStyle = "rgba(255, 255, 255, 0.55)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(rimX - rimW / 2, rimY);
        ctx.lineTo(rimX - 15, rimY + 42);
        ctx.lineTo(rimX + 15, rimY + 42);
        ctx.lineTo(rimX + rimW / 2, rimY);
        ctx.stroke();

        // Mesh cross weave lines
        ctx.strokeStyle = "rgba(255, 255, 255, 0.28)";
        ctx.beginPath();
        ctx.moveTo(rimX - 30, rimY + 15);
        ctx.lineTo(rimX + 22, rimY + 28);
        ctx.moveTo(rimX + 30, rimY + 15);
        ctx.lineTo(rimX - 22, rimY + 28);
        ctx.stroke();

        // Hoop Back Rim
        ctx.strokeStyle = "#ea580c"; ctx.lineWidth = 6;
        ctx.beginPath(); 
        ctx.ellipse(rimX, rimY, rimW / 2, 10, 0, Math.PI, Math.PI * 2);
        ctx.stroke();

        const ball = game.current.ball;
        const bScale = Math.max(0.5, 1 - (550 - ball.y) / 800);
        const vRadius = ball.radius * bScale;

        // Dynamic Soft Altitude Shadow on floor
        const shadowY = 550;
        const altitude = shadowY - ball.y;
        if (altitude > -20) {
            ctx.save();
            const shadowScale = Math.max(0.2, 1 - altitude / 450);
            const shadowOpacity = Math.max(0.01, 0.38 * shadowScale);
            ctx.fillStyle = `rgba(0, 0, 0, ${shadowOpacity})`;
            ctx.beginPath();
            ctx.ellipse(ball.x, shadowY, vRadius * 1.5 * shadowScale, vRadius * 0.45 * shadowScale, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        }

        // 3D Specular Basketball with curved seams
        const ballGrad = ctx.createRadialGradient(
            ball.x - vRadius * 0.35, 
            ball.y - vRadius * 0.35, 
            vRadius * 0.1, 
            ball.x, 
            ball.y, 
            vRadius
        );
        ballGrad.addColorStop(0, "#ffaa55");
        ballGrad.addColorStop(0.5, "#ea580c");
        ballGrad.addColorStop(0.85, "#c2410c");
        ballGrad.addColorStop(1, "#7c2d12");

        ctx.fillStyle = ballGrad;
        ctx.beginPath();
        ctx.arc(ball.x, ball.y, vRadius, 0, Math.PI * 2);
        ctx.fill();

        // Spherical black seams
        ctx.strokeStyle = "#181512";
        ctx.lineWidth = 2.2 * bScale;
        
        ctx.beginPath();
        ctx.moveTo(ball.x - vRadius, ball.y);
        ctx.quadraticCurveTo(ball.x, ball.y + vRadius * 0.22, ball.x + vRadius, ball.y);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(ball.x, ball.y - vRadius);
        ctx.quadraticCurveTo(ball.x + vRadius * 0.22, ball.y, ball.x, ball.y + vRadius);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(ball.x - vRadius * 0.7, ball.y - vRadius * 0.7);
        ctx.quadraticCurveTo(ball.x - vRadius * 0.1, ball.y, ball.x - vRadius * 0.7, ball.y + vRadius * 0.7);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(ball.x + vRadius * 0.7, ball.y - vRadius * 0.7);
        ctx.quadraticCurveTo(ball.x + vRadius * 0.1, ball.y, ball.x + vRadius * 0.7, ball.y + vRadius * 0.7);
        ctx.stroke();

        // Specular glaze reflection highlights
        const specularGrad = ctx.createRadialGradient(
            ball.x - vRadius * 0.4, 
            ball.y - vRadius * 0.4, 
            1, 
            ball.x - vRadius * 0.4, 
            ball.y - vRadius * 0.4, 
            vRadius * 0.75
        );
        specularGrad.addColorStop(0, "rgba(255, 255, 255, 0.45)");
        specularGrad.addColorStop(1, "rgba(255, 255, 255, 0)");
        ctx.fillStyle = specularGrad;
        ctx.beginPath();
        ctx.arc(ball.x - vRadius * 0.4, ball.y - vRadius * 0.4, vRadius * 0.7, 0, Math.PI * 2);
        ctx.fill();

        // Front Rim
        ctx.strokeStyle = "#ea580c"; ctx.lineWidth = 6;
        ctx.beginPath(); 
        ctx.ellipse(rimX, rimY, rimW / 2, 10, 0, 0, Math.PI); 
        ctx.stroke();

        // Aiming Trajectory Indicator
        if (touchRef.current.isTouching && !ball.active) {
            const dx = touchRef.current.currentX - touchRef.current.startX;
            const dy = touchRef.current.currentY - touchRef.current.startY;
            if (dy < -5) {
                ctx.strokeStyle = "rgba(255, 255, 255, 0.48)";
                ctx.lineWidth = 3.5;
                ctx.setLineDash([6, 6]);
                ctx.beginPath();
                ctx.moveTo(ball.x, ball.y);
                ctx.lineTo(ball.x + dx * 1.5, ball.y + dy * 1.5);
                ctx.stroke();
                ctx.setLineDash([]);
            }
        }

        ctx.restore();
    };

    useEffect(() => {
        if (gameStarted) {
            game.current.lastFrame = performance.now();
            requestAnimationFrame(update);
        }
    }, [gameStarted, update]);

    const handleRetry = () => {
        setGameOver(false);
        setScore(0);
        resetBall();
    };

    return (
        <div 
            className="bm_container" 
            onMouseDown={handleStart} onTouchStart={handleStart}
            onMouseMove={handleMove} onTouchMove={handleMove}
            onMouseUp={handleEnd} onTouchEnd={handleEnd}
            onMouseLeave={handleEnd}
        >
            <div className="te_header">
                <button onClick={onBack} className="te_back">← Quit</button>
                <button onClick={() => { warmUpAudio(); setShowInfo(true); }} className="te_info_btn" title="How to Play">💡 Help</button>
                <div className="te_score te_desktop-score">SCORE: {score}</div>
            </div>
            
            <div className="te_mobile-score-display">
                <span className="label">SCORE</span>
                <span className="val">{score}</span>
            </div>
            
            <canvas ref={canvasRef} width={dimensions.width} height={dimensions.height} />
            
            {feedback && <div className="bm_feedback">{feedback}</div>}
 
            {!gameStarted && (
                <div className="te_overlay">
                    <div className="te_overlay_card">
                        <div className="te_icon_bounce">🏀</div>
                        <h1>FLICK <span>HOOP</span></h1>
                        <p>Swipe up to shoot the ball!</p>
                        <div className="te_menu_actions">
                            <button className="te_btn_primary" onClick={() => { warmUpAudio(); setGameStarted(true); setScore(0); }}>LET'S SHOOT</button>
                            <button className="te_btn_secondary" onClick={() => { warmUpAudio(); setShowInfo(true); }}>HOW TO PLAY</button>
                        </div>
                    </div>
                </div>
            )}
            
            {showInfo && (
                <div className="te_overlay te_info_overlay">
                    <div className="te_overlay_card te_info_card">
                        <div className="te_info_header">
                            <h2>HOW TO PLAY 🏀</h2>
                            <h3>રમતની માહિતી અને નિયમો</h3>
                        </div>
                        
                        <div className="te_info_body">
                            <div className="te_info_step">
                                <span className="step_num">1</span>
                                <div className="step_desc">
                                    <p><strong>Flick / Swipe Up:</strong> Touch the basketball, then drag or swipe your finger/mouse upwards towards the hoop.</p>
                                    <p className="step_gu"><strong>ઉપર સ્વાઇપ કરો:</strong> બાસ્કેટબોલને ટચ કરી આંગળી કે માઉસને બાસ્કેટ (Hoop) તરફ સ્લાઇડ કરો.</p>
                                </div>
                            </div>
                            
                            <div className="te_info_step">
                                <span className="step_num">2</span>
                                <div className="step_desc">
                                    <p><strong>Aiming Trajectory:</strong> Use the dotted line to adjust shoot angle and flick speed. Faster swipes throw the ball harder!</p>
                                    <p className="step_gu"><strong>દિશા અને સ્પીડ:</strong> ડ્રોઇંગ લાઇનથી એન્ગલ સેટ કરો. ઝડપથી સ્વાઇપ કરશો તો બોલ વધુ તેજ ગતિએ જશે!</p>
                                </div>
                            </div>

                            <div className="te_info_step">
                                <span className="step_num">3</span>
                                <div className="step_desc">
                                    <p><strong>Score Points:</strong> Put the ball cleanly into the basket to score 1 point. Listen to the rewarding Swish sound!</p>
                                    <p className="step_gu"><strong>પોઈન્ટ મેળવો:</strong> બોલ બાસ્કેટની અંદર જતાં જ તમને ૧ પોઈન્ટ મળશે.</p>
                                </div>
                            </div>
                        </div>
                        
                        <button className="te_btn_primary te_info_close" onClick={() => setShowInfo(false)}>
                            GOT IT • સમજાઈ ગયું
                        </button>
                    </div>
                </div>
            )}
            
            {gameOver && (
                <div className="te_overlay">
                    <div className="te_overlay_card">
                        <div className="te_icon_shake">🏆</div>
                        <h1>GAME OVER</h1>
                        <p>You scored <strong>{score}</strong> points!</p>
                        <button className="te_btn_primary" onClick={handleRetry}>LET'S SHOOT AGAIN</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BasketballFlick;
